// Add words: single + bulk with AI auto-fill via backend.
import React, { useState } from 'react';
import { Plus, Stack, Sparkle, CheckCircle, Warning } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { generateWord, generateBatch } from '../lib/api';
import { wordsApi } from '../lib/store';
import { ADD } from '../constants/testIds';

const TYPE_OPTIONS = [
  { v: 'auto', t: 'Otomatik algıla' },
  { v: 'noun', t: 'İsim (Nomen)' },
  { v: 'adjective', t: 'Sıfat (Adjektiv)' },
  { v: 'adverb', t: 'Zarf / Edat (Adverb · Präposition)' },
  { v: 'verb', t: 'Fiil (Verb)' },
];

export default function AddWordsPage() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-12 py-12">
      <div className="overline mb-3">KELİME EKLE · WORTSCHATZ</div>
      <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none mb-12">
        Yeni kart oluştur.
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border">
        <SinglePanel />
        <BulkPanel />
      </div>
    </section>
  );
}

// ----- SINGLE --------------------------------------------------------------
function SinglePanel() {
  const [german, setGerman] = useState('');
  const [type, setType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!german.trim()) {
      toast.error('Almanca kelime gerekli.');
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const data = await generateWord(german.trim(), type);
      wordsApi.add(data);
      setFeedback({ kind: 'ok', msg: `"${data.german}" eklendi (${data.type}).` });
      toast.success(`${data.german} eklendi`);
      setGerman('');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Bilinmeyen hata';
      setFeedback({ kind: 'err', msg });
      toast.error('Eklenemedi: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-10 sm:p-12 border-b lg:border-b-0 lg:border-r border-border">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 grid place-items-center border border-foreground">
          <Plus size={20} weight="bold" />
        </div>
        <div>
          <div className="overline">MODUS · 01</div>
          <h2 className="font-display font-black text-2xl tracking-tight">Tek Kelime</h2>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="g-input" className="overline mb-2 block">Almanca Kelime</Label>
          <Input
            id="g-input"
            data-testid={ADD.germanInput}
            placeholder="z. B. Tisch, schön, fahren, schnell …"
            value={german}
            onChange={(e) => setGerman(e.target.value)}
            disabled={loading}
            className="rounded-none h-12 text-lg font-display"
          />
        </div>

        <div>
          <Label className="overline mb-2 block">Kelime Türü</Label>
          <Select value={type} onValueChange={setType} disabled={loading}>
            <SelectTrigger data-testid={ADD.typeSelect} className="rounded-none h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v} className="rounded-none">{o.t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          data-testid={ADD.submitBtn}
          disabled={loading}
          className="rounded-none h-12 w-full btn-tactile"
        >
          {loading
            ? <>Üretiliyor…</>
            : <><Sparkle size={18} weight="duotone" className="mr-2" /> KI ile Oluştur</>}
        </Button>

        {feedback && (
          <div
            data-testid={ADD.feedback}
            className={`flex items-start gap-2 text-sm border p-3 ${
              feedback.kind === 'ok'
                ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300'
                : 'border-rose-500 text-rose-700 dark:text-rose-300'
            }`}
          >
            {feedback.kind === 'ok'
              ? <CheckCircle size={18} weight="fill" />
              : <Warning size={18} weight="fill" />}
            <span>{feedback.msg}</span>
          </div>
        )}
      </div>

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
        Yapay zekâ kelimenin türüne göre Türkçe karşılığını, artikeli,
        çoğulu ve B2 seviyesinde örnek cümleleri otomatik üretir.
      </p>
    </form>
  );
}

// ----- BULK ----------------------------------------------------------------
function BulkPanel() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const words = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (words.length === 0) {
      toast.error('En az bir kelime yaz.');
      return;
    }
    if (words.length > 50) {
      toast.error('Bir seferde en fazla 50 kelime ekleyebilirsin.');
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const data = await generateBatch(words);
      const ok = data.results.filter((r) => r.ok).map((r) => r.data);
      const failed = data.results.filter((r) => !r.ok);
      if (ok.length) wordsApi.addMany(ok);
      setReport({ added: ok.length, failed });
      if (ok.length) toast.success(`${ok.length} kelime eklendi`);
      if (failed.length) toast.error(`${failed.length} kelime başarısız`);
      setText('');
    } catch (err) {
      toast.error('Toplu işlem başarısız: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const count = text.split(/\r?\n/).filter((s) => s.trim()).length;

  return (
    <form onSubmit={submit} className="p-10 sm:p-12">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 grid place-items-center border border-foreground">
          <Stack size={20} weight="bold" />
        </div>
        <div>
          <div className="overline">MODUS · 02</div>
          <h2 className="font-display font-black text-2xl tracking-tight">Toplu Yükleme</h2>
        </div>
      </div>

      <div className="space-y-4">
        <Label className="overline mb-2 block">
          Her satıra bir Almanca kelime ({count} / 50)
        </Label>
        <Textarea
          data-testid={ADD.bulkTextarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          disabled={loading}
          placeholder={'Haus\nschön\nfahren\nschnell\nVerantwortung'}
          className="rounded-none font-mono-plex text-base leading-relaxed min-h-[260px]"
        />

        <Button
          type="submit"
          data-testid={ADD.bulkSubmitBtn}
          disabled={loading || count === 0}
          className="rounded-none h-12 w-full btn-tactile"
        >
          {loading
            ? <>İşleniyor… ({count} kelime)</>
            : <><Sparkle size={18} weight="duotone" className="mr-2" /> Hepsini KI ile Oluştur</>}
        </Button>

        {report && (
          <div className="border border-border p-4 text-sm">
            <div className="flex items-center gap-2 mb-1 text-emerald-600 dark:text-emerald-400">
              <CheckCircle size={16} weight="fill" /> {report.added} kelime kart havuzuna eklendi.
            </div>
            {report.failed.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-1">
                  <Warning size={16} weight="fill" /> {report.failed.length} başarısız:
                </div>
                <ul className="ml-6 list-disc text-muted-foreground">
                  {report.failed.map((f) => (
                    <li key={f.german}><b>{f.german}</b> — {f.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
        Toplu modda sistem her kelimenin türünü kendi algılar, eş zamanlı olarak
        Türkçe çevirileri, gramer ekleri ve B2 cümlelerini üretir.
      </p>
    </form>
  );
}
